import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  CheckCircle,
  XCircle,
  Smartphone,
  Search,
  ChevronDown,
  ChevronUp,
} from "react-feather";
import { useGet, useGetLazy } from "../../api";
import { ISamsungCheckResponse } from "../../interfaces";

const HISTORY_KEY = "samsung_check_history";
const MAX_HISTORY = 10;

interface HistoryEntry {
  domain: string;
  countryCode: string;
}

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveHistory(entry: HistoryEntry): HistoryEntry[] {
  const prev = loadHistory().filter(
    (h) => !(h.domain === entry.domain && h.countryCode === entry.countryCode)
  );
  const next = [entry, ...prev].slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
}

export const Samsung: React.FC = () => {
  const [domain, setDomain] = useState("");
  const [countryCode, setCountryCode] = useState("UK");
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);

  const { data, loading, error, execute } = useGetLazy<ISamsungCheckResponse>();

  const runCheck = (d: string, cc: string) => {
    const cleanDomain = d
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    const cleanCc = cc.trim().toUpperCase();
    execute(
      `kindred/samsung-check?domain=${encodeURIComponent(cleanDomain)}&countryCode=${encodeURIComponent(cleanCc)}`
    );
    setHistory(saveHistory({ domain: cleanDomain, countryCode: cleanCc }));
  };

  const handleCheck = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim() || !countryCode.trim()) return;
    runCheck(domain, countryCode);
  };

  return (
    <div>
      <h4 className="mb-3">
        <Smartphone size={20} className="me-2" />
        Samsung Eligibility Check
      </h4>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleCheck}>
            <Row className="align-items-end g-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Domain</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. brickzonehub.co.uk"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Country Code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. UK"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    maxLength={2}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!domain.trim() || !countryCode.trim() || loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <Search size={14} className="me-1" />
                  )}
                  Check Samsung Eligibility
                </Button>
              </Col>
            </Row>
          </Form>
          {history.length > 0 && (
            <div className="mt-3">
              <div className="small text-muted mb-1">Recent searches:</div>
              <div className="d-flex flex-wrap gap-1">
                {history.map((h) => (
                  <Badge
                    key={`${h.domain}:${h.countryCode}`}
                    bg="secondary"
                    role="button"
                    style={{ cursor: "pointer", fontWeight: "normal" }}
                    onClick={() => {
                      setDomain(h.domain);
                      setCountryCode(h.countryCode);
                      runCheck(h.domain, h.countryCode);
                    }}
                  >
                    {h.domain} · {h.countryCode}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          Failed to check Samsung eligibility. Please try again.
        </Alert>
      )}

      {data && <SamsungResult data={data} />}
    </div>
  );
};

const SamsungResult: React.FC<{ data: ISamsungCheckResponse }> = ({ data }) => {
  const [dealOpen, setDealOpen] = useState(true);
  const [autoOpen, setAutoOpen] = useState(true);
  const [manualOpen, setManualOpen] = useState(true);
  const [couponsExpanded, setCouponsExpanded] = useState(false);
  const [offersExpanded, setOffersExpanded] = useState(false);

  return (
    <div>
      {/* Overall verdict */}
      <Alert variant={data.showForSamsung ? "success" : "danger"}>
        <div className="d-flex align-items-center gap-2">
          {data.showForSamsung ? (
            <CheckCircle size={20} />
          ) : (
            <XCircle size={20} />
          )}
          <strong>
            {data.domain} ({data.countryCode})
          </strong>
          {data.showForSamsung
            ? " will show for Samsung"
            : " will NOT show for Samsung"}
        </div>
      </Alert>

      <Row className="g-3">
        {/* Deal Section */}
        <Col md={4}>
          <Card
            className={`h-100 ${data.hasDeal ? "border-success" : "border-danger"}`}
          >
            <Card.Header
              role="button"
              onClick={() => setDealOpen((o) => !o)}
              className={`small fw-bold d-flex justify-content-between align-items-center ${data.hasDeal ? "bg-success text-white" : "bg-danger text-white"}`}
            >
              <span>
                {data.hasDeal ? (
                  <CheckCircle size={14} className="me-1" />
                ) : (
                  <XCircle size={14} className="me-1" />
                )}
                Deal Status
              </span>
              {dealOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Card.Header>
            <Collapse in={dealOpen}>
              <Card.Body>
                {data.hasDeal && data.dealInfo ? (
                  <div className="small">
                    <div>
                      <strong>ID:</strong>{" "}
                      <a
                        href={`/kindred/deals/${data.dealInfo.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <code>{data.dealInfo.id}</code>
                      </a>
                    </div>
                    <div>
                      <strong>Store ID:</strong>{" "}
                      <a
                        href={`/kindred/brands/${data.dealInfo.storeId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <code>{data.dealInfo.storeId}</code>
                      </a>
                    </div>
                    <div>
                      <strong>Store:</strong> {data.dealInfo.storeName}
                    </div>
                    <div>
                      <strong>Cashback:</strong> {data.dealInfo.cashback}
                      {data.dealInfo.cashbackType === "Percentage"
                        ? "%"
                        : ` ${data.dealInfo.cashbackType}`}
                      {data.dealInfo.currency
                        ? ` (${data.dealInfo.currency})`
                        : ""}
                    </div>
                    <div>
                      <strong>Start:</strong>{" "}
                      {new Date(data.dealInfo.startDate).toLocaleDateString()}
                    </div>
                    <div>
                      <strong>End:</strong>{" "}
                      {new Date(data.dealInfo.endDate).toLocaleDateString()}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small">
                    No active deal found. Samsung requires a valid deal.
                  </div>
                )}
              </Card.Body>
            </Collapse>
          </Card>
        </Col>

        {/* Automatic Flow */}
        <Col md={4}>
          <Card
            className={`h-100 ${data.automaticFlow.eligible ? "border-success" : "border-secondary"}`}
          >
            <Card.Header
              role="button"
              onClick={() => setAutoOpen((o) => !o)}
              className={`small fw-bold d-flex justify-content-between align-items-center ${data.automaticFlow.eligible ? "bg-success text-white" : "bg-secondary text-white"}`}
            >
              <span>
                {data.automaticFlow.eligible ? (
                  <CheckCircle size={14} className="me-1" />
                ) : (
                  <XCircle size={14} className="me-1" />
                )}
                Automatic Coupon Flow
              </span>
              {autoOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Card.Header>
            <Collapse in={autoOpen}>
              <Card.Body>
                <div className="small">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Sitewide Coupons:</span>
                    <Badge
                      bg={
                        data.automaticFlow.sitewideCouponCount > 0
                          ? "success"
                          : "danger"
                      }
                    >
                      {data.automaticFlow.sitewideCouponCount}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Auto Templates:</span>
                    <Badge
                      bg={
                        data.automaticFlow.templateCount > 0
                          ? "success"
                          : "danger"
                      }
                    >
                      {data.automaticFlow.templateCount}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>SKU Templates:</span>
                    <Badge
                      bg={
                        data.automaticFlow.skuTemplateCount > 0
                          ? "success"
                          : "danger"
                      }
                    >
                      {data.automaticFlow.skuTemplateCount}
                    </Badge>
                  </div>
                  {!data.automaticFlow.eligible && (
                    <div className="text-muted mt-2">
                      {!data.hasDeal
                        ? "Requires a valid deal first."
                        : data.automaticFlow.sitewideCouponCount === 0
                          ? "Needs at least 1 sitewide coupon."
                          : data.automaticFlow.templateCount === 0
                            ? "Needs at least 1 auto template."
                            : "Needs at least 1 SKU template."}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>

        {/* Manual Flow */}
        <Col md={4}>
          <Card
            className={`h-100 ${data.manualFlow.eligible ? "border-success" : "border-secondary"}`}
          >
            <Card.Header
              role="button"
              onClick={() => setManualOpen((o) => !o)}
              className={`small fw-bold d-flex justify-content-between align-items-center ${data.manualFlow.eligible ? "bg-success text-white" : "bg-secondary text-white"}`}
            >
              <span>
                {data.manualFlow.eligible ? (
                  <CheckCircle size={14} className="me-1" />
                ) : (
                  <XCircle size={14} className="me-1" />
                )}
                Manual List
              </span>
              {manualOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Card.Header>
            <Collapse in={manualOpen}>
              <Card.Body>
                <div className="small">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span>Coupons:</span>
                    <div className="d-flex align-items-center gap-2">
                      <Badge
                        bg={
                          data.manualFlow.couponCount > 0 ? "info" : "secondary"
                        }
                      >
                        {data.manualFlow.couponCount}
                      </Badge>
                      {data.manualFlow.couponCount > 0 && (
                        <span
                          role="button"
                          className="text-primary"
                          onClick={() => setCouponsExpanded((o) => !o)}
                        >
                          {couponsExpanded ? (
                            <ChevronUp size={13} />
                          ) : (
                            <ChevronDown size={13} />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <Collapse in={couponsExpanded}>
                    <div className="mb-2">
                      {data.manualFlow.coupons.map((c) => (
                        <div
                          key={c.id}
                          className="border rounded p-1 mb-1 bg-light"
                          style={{ fontSize: 11 }}
                        >
                          <div className="d-flex justify-content-between gap-1">
                            <a
                              href={`/kindred/coupons/${c.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <code className="fw-bold">{c.code}</code>
                            </a>
                            <div className="d-flex gap-1">
                              {c.isSiteWide && (
                                <Badge bg="success" style={{ fontSize: 10 }}>
                                  sitewide
                                </Badge>
                              )}
                              <span
                                style={{
                                  fontSize: 10,
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontWeight: 600,
                                  ...(c.role === 1
                                    ? { background: "#0d6efd", color: "#fff" }
                                    : c.role === 2
                                      ? { background: "#fd7e14", color: "#fff" }
                                      : {
                                          background: "#6c757d",
                                          color: "#fff",
                                        }),
                                }}
                              >
                                {c.role === 1
                                  ? "AutoCoupon"
                                  : c.role === 2
                                    ? "Fallback"
                                    : "Listed"}
                              </span>
                            </div>
                          </div>
                          {c.summary && (
                            <div className="text-muted">{c.summary}</div>
                          )}
                          <div className="text-muted">
                            brand:{" "}
                            {c.brandId ? (
                              <a
                                href={`/kindred/brands/${c.brandId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {c.brandName ?? c.brandId}
                              </a>
                            ) : (
                              <span>unknown</span>
                            )}
                          </div>
                          <div className="text-muted">
                            priority: {c.priority}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span>Offers:</span>
                    <div className="d-flex align-items-center gap-2">
                      <Badge
                        bg={
                          data.manualFlow.offerCount > 0
                            ? "warning"
                            : "secondary"
                        }
                        text={
                          data.manualFlow.offerCount > 0 ? "dark" : undefined
                        }
                      >
                        {data.manualFlow.offerCount}
                      </Badge>
                      {data.manualFlow.offerCount > 0 && (
                        <span
                          role="button"
                          className="text-primary"
                          onClick={() => setOffersExpanded((o) => !o)}
                        >
                          {offersExpanded ? (
                            <ChevronUp size={13} />
                          ) : (
                            <ChevronDown size={13} />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <Collapse in={offersExpanded}>
                    <div className="mb-2">
                      {data.manualFlow.offers.map((o, i) => (
                        <div
                          key={String(o["id"] ?? i)}
                          className="border rounded p-1 mb-1 bg-light"
                          style={{ fontSize: 11 }}
                        >
                          {o["id"] != null && (
                            <div className="mb-1">
                              <a
                                href={`/kindred/offers/${String(o["id"])}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <code style={{ fontSize: 11 }}>
                                  {String(o["id"])}
                                </code>
                              </a>
                            </div>
                          )}
                          <pre
                            className="mb-0"
                            style={{ fontSize: 11, whiteSpace: "pre-wrap" }}
                          >
                            {JSON.stringify(o, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                  <div className="d-flex justify-content-between">
                    <span>List Type:</span>
                    <Badge bg="primary">
                      {data.manualFlow.listType === "coupons_only"
                        ? "Coupons Only"
                        : data.manualFlow.listType === "offers_only"
                          ? "Offers Only"
                          : data.manualFlow.listType === "both"
                            ? "Coupons + Offers"
                            : "None"}
                    </Badge>
                  </div>
                  {!data.manualFlow.eligible && (
                    <div className="text-muted mt-2">
                      {!data.hasDeal
                        ? "Requires a valid deal first."
                        : "Needs at least some coupons or offers."}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
